/**
 * Rodné číslo, decoded and checked per zákon č. 133/2000 Sb. § 13.
 *
 * The twin of `internal/birthnumber` (Go), and deliberately so: the server
 * is the authority, but a leader who has just typed a number should learn
 * it is wrong at the field rather than at submit. Both sides are pinned to
 * `internal/birthnumber/testdata/vectors.json`, which both test suites
 * read, so they cannot drift apart quietly.
 *
 * The reasoning behind every rule, the measurements over the federation's
 * real data that settled the contested ones, and the provenance of the
 * vectors are in `docs/rodne-cislo.md`. Two rules look wrong until read:
 *
 * - A nine-digit number is NOT checksummed. § 13 odst. 4 says of them that
 *   they „nesplňují podmínku dělitelnosti jedenácti".
 * - The remainder-10 convention is accepted in ANY year, despite the 1985
 *   directive that created it. Three current members born 2010, 2014 and
 *   2016 hold such numbers.
 */

export type Sex = "M" | "F";

export type BirthNumberProblem =
  | "empty"
  | "length"
  | "month"
  | "date"
  | "insurance_number"
  | "nine_digit_after_1953"
  | "extended_series_too_early"
  | "checksum";

export interface BirthNumberInfo {
  /** Digits only, with the slash and any stray punctuation removed. */
  digits: string;
  /** The date the number encodes, as `YYYY-MM-DD`. */
  birthDate: string;
  sex: Sex;
  /** The § 13 odst. 5 series, i.e. a month carrying +20 or +70. */
  extended: boolean;
  /** The pre-1985 remainder-10 convention; valid, but worth surfacing. */
  legacyCheckDigit: boolean;
}

/**
 * Strips everything that is not a digit. The slash is presentational, and
 * real sheets carry stray spaces and the odd „ú" where a Czech keyboard
 * slipped off the slash.
 */
export function birthNumberDigits(value: string): string {
  return (value ?? "").replace(/\D/g, "");
}

/**
 * Decodes `value`, or names what is wrong with it. Returns a discriminated
 * union rather than throwing: every caller here is a form field, and a
 * field asks a question rather than reporting an exception.
 */
export function parseBirthNumber(
  value: string,
): { ok: true; info: BirthNumberInfo } | { ok: false; problem: BirthNumberProblem } {
  const d = birthNumberDigits(value);
  if (d === "") return { ok: false, problem: "empty" };
  if (d.length !== 9 && d.length !== 10) return { ok: false, problem: "length" };

  const yy = Number(d.slice(0, 2));
  const rawMonth = Number(d.slice(2, 4));
  const rawDay = Number(d.slice(4, 6));

  const decoded = decodeMonth(rawMonth);
  if (!decoded) return { ok: false, problem: "month" };
  const { month, sex, extended } = decoded;

  // No century is encoded anywhere; length is the only carrier. § 13 odst. 4
  // gives nine digits to births before 1954, odst. 3 gives ten to 1954
  // onwards, so a nine-digit number reading 1954+ is a truncated modern one
  // rather than a very old member.
  const year = d.length === 10 && yy < 54 ? 2000 + yy : 1900 + yy;
  if (d.length === 9 && year >= 1954) return { ok: false, problem: "nine_digit_after_1953" };

  if (rawDay > 31 && looksLikeInsuranceNumber(rawDay, extended, d)) {
    return { ok: false, problem: "insurance_number" };
  }

  if (!isRealDate(year, month, rawDay)) return { ok: false, problem: "date" };

  // zákon č. 53/2004 Sb. introduced the +20/+70 series with effect from
  // 1 April 2004, so it cannot describe an earlier birth. The gate is the
  // birth YEAR, not that date: a number for somebody born in January 2004
  // may legitimately come from the extended series later.
  if (extended && year < 2004) return { ok: false, problem: "extended_series_too_early" };

  let legacyCheckDigit = false;
  if (d.length === 10) {
    const check = checksum(d);
    if (!check.ok) return { ok: false, problem: "checksum" };
    legacyCheckDigit = check.legacy;
  }

  return {
    ok: true,
    info: {
      digits: d,
      birthDate: `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(rawDay).padStart(2, "0")}`,
      sex,
      extended,
      legacyCheckDigit,
    },
  };
}

/**
 * Whether the number matches a birth date recorded beside it, comparing the
 * WHOLE date, year included.
 *
 * `docs/history-import.md` compares only day and month for a ten-digit
 * number, and that is right there: its question is which of two disagreeing
 * sheets to believe, with the number as a tiebreaker over a known day/month
 * swap. The question here is whether one record is self-consistent, and the
 * year matters most — věková kategorie is derived from the birth date while
 * the documents behind it carry the rodné číslo, so a year that disagrees
 * puts a competitor in the wrong category. Twenty well-formed numbers in the
 * federation's sheets disagree on the year alone, seven by exactly ten years.
 *
 * `birthDate` is the API's date-only form, `YYYY-MM-DD`.
 */
export function birthNumberAgrees(info: BirthNumberInfo, birthDate: string): boolean {
  if (!birthDate) return false;
  return info.birthDate === birthDate.slice(0, 10);
}

const PROBLEM_MESSAGES: Record<BirthNumberProblem, string> = {
  empty: "Zadejte rodné číslo",
  length: "Rodné číslo má devět nebo deset číslic",
  month: "Rodné číslo neodpovídá žádnému datu narození; zkontrolujte ho",
  date: "Rodné číslo neodpovídá žádnému datu narození; zkontrolujte ho",
  insurance_number: "Toto vypadá jako číslo pojištěnce, ne jako rodné číslo",
  nine_digit_after_1953: "Rodné číslo je o číslici kratší, než má být",
  extended_series_too_early: "Rodné číslo neodpovídá žádnému datu narození; zkontrolujte ho",
  checksum: "Rodné číslo neprošlo kontrolou, nejspíš je v něm překlep",
};

/**
 * Checks `value` as a rodné číslo for somebody born on `birthDate` and
 * returns the Czech description of what is wrong, or `null` when nothing
 * is. Mirrors `birthnumber.Problem` in Go, wording included.
 *
 * An empty `birthDate` skips the agreement check rather than failing it:
 * the form validates a missing birth date on its own field, and reporting
 * it twice reads as two separate mistakes.
 */
export function birthNumberProblem(value: string, birthDate?: string): string | null {
  const parsed = parseBirthNumber(value);
  if (!parsed.ok) return PROBLEM_MESSAGES[parsed.problem];
  if (!birthDate || birthNumberAgrees(parsed.info, birthDate)) return null;
  return (
    `Rodné číslo odpovídá datu narození ${czDate(parsed.info.birthDate)}, ` +
    `evidované je ${czDate(birthDate)}`
  );
}

/**
 * The live-warning form of {@link birthNumberProblem}: silent on a blank
 * field.
 *
 * The two differ on purpose. `birthNumberProblem` mirrors the Go
 * `birthnumber.Problem` and reports an empty value, because the server uses
 * it to REFUSE a leader with no number. A hint under a field the user has
 * not filled in yet is only nagging, and the field's own required rule
 * already says the same sentence at submit.
 */
export function birthNumberHint(
  value: string,
  birthDate?: string,
  opts?: { typing?: boolean },
): string | null {
  const digits = birthNumberDigits(value);
  if (digits === "") return null;

  // While the field still has FOCUS, say nothing about a value shorter than
  // the form the birth date requires: § 13 gives ten digits to a birth from
  // 1954 on and nine to anything earlier, so anything shorter is half-typed
  // rather than wrong.
  //
  // Without that, the ninth keystroke of a modern number is actively
  // misleading, not merely noisy. Typing 0854110015 for somebody born in
  // 2008, the prefix 085411001 is a perfectly valid NINE-digit number
  // encoding 11. 4. 1908 — so one keystroke before the user finishes, the
  // field asserts „Rodné číslo odpovídá datu narození 11. 4. 1908" about a
  // number that is about to be completely correct. Any birth year from 2000
  // on hits it.
  //
  // The moment focus LEAVES, judge whatever is there, however short. This
  // used to key off length alone, with a comment claiming the required rule
  // and the server caught a too-short number between them. Neither does:
  // both check only non-blankness, so „205407" was accepted by the schema,
  // by the server, and silently by this function too. Strict validation on
  // write is MR 4; until then this hint is the only thing that looks.
  if (opts?.typing && digits.length < expectedLength(birthDate)) return null;

  return birthNumberProblem(value, birthDate);
}

/** How many digits § 13 gives a birth on `birthDate`; 9 when unknown. */
function expectedLength(birthDate?: string): number {
  if (!birthDate) return 9;
  const year = Number(birthDate.slice(0, 4));
  return Number.isFinite(year) && year < 1954 ? 9 : 10;
}

function decodeMonth(raw: number): { month: number; sex: Sex; extended: boolean } | null {
  // Anything outside the four legal bands is refused rather than folded:
  // python-stdnum's `% 50 % 20` shortcut quietly maps 41-49 and 91-99 onto
  // real months, inventing valid numbers out of impossible ones.
  if (raw >= 1 && raw <= 12) return { month: raw, sex: "M", extended: false };
  if (raw >= 51 && raw <= 62) return { month: raw - 50, sex: "F", extended: false };
  if (raw >= 21 && raw <= 32) return { month: raw - 20, sex: "M", extended: true };
  if (raw >= 71 && raw <= 82) return { month: raw - 70, sex: "F", extended: true };
  return null;
}

/**
 * Two corroborating signals, never one. A shifted day on its own — the
 * older insurer scheme is simply day + 50 — is indistinguishable from a
 * mistyped day, and typos are far commoner: the federation's sheets carry
 * out-of-range days and not one insurance number.
 */
function looksLikeInsuranceNumber(rawDay: number, extended: boolean, d: string): boolean {
  if (rawDay >= 41 && rawDay <= 71 && d.length === 10 && Number(d.slice(6, 10)) >= 6000) {
    return true;
  }
  return extended && rawDay >= 51 && rawDay <= 81;
}

function isRealDate(year: number, month: number, day: number): boolean {
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day
  );
}

/** § 13 odst. 3, plus the remainder-10 convention. Ten digits only. */
function checksum(d: string): { ok: boolean; legacy: boolean } {
  // Computed digit by digit: a ten-digit number exceeds Number.MAX_SAFE_INTEGER
  // territory less obviously than it looks, and modular arithmetic avoids the
  // question entirely.
  const mod = (s: string) => [...s].reduce((n, c) => (n * 10 + Number(c)) % 11, 0);
  if (mod(d) === 0) return { ok: true, legacy: false };
  if (mod(d.slice(0, 9)) === 10 && d[9] === "0") return { ok: true, legacy: true };
  return { ok: false, legacy: false };
}

function czDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)}. ${Number(m)}. ${y}`;
}
